<template>
  <div class="action-card">
    <div class="card-title">{{ titleMap[intent] }}</div>

    <!-- 创建待办 -->
    <template v-if="intent === 'create_todo'">
      <div class="form-item">
        <label>标题</label>
        <el-input v-model="todoForm.title" placeholder="待办标题" />
      </div>
      <div class="form-item">
        <label>优先级</label>
        <el-select v-model="todoForm.priority" style="width: 100%">
          <el-option label="低" value="low" />
          <el-option label="中" value="medium" />
          <el-option label="高" value="high" />
          <el-option label="紧急" value="urgent" />
        </el-select>
      </div>
      <div class="form-item">
        <label>截止日期</label>
        <el-date-picker v-model="todoForm.dueDate" type="date" value-format="YYYY-MM-DD" placeholder="可选" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>截止时间</label>
        <el-time-picker v-model="todoForm.dueTime" format="HH:mm" value-format="HH:mm" placeholder="可选" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>负责人</label>
        <el-select v-model="todoForm.assigneeId" style="width: 100%">
          <el-option :label="`我自己${self?.employeeName ? `（${self.employeeName}）` : ''}`" :value="0" />
          <el-option v-for="e in candidates?.assignees || []" :key="e.id" :label="e.name" :value="e.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>备注</label>
        <el-input v-model="todoForm.description" type="textarea" :rows="2" placeholder="可选" />
      </div>
    </template>

    <!-- 创建安装工单 -->
    <template v-else-if="intent === 'create_work_order'">
      <div class="form-item">
        <label>客户 *</label>
        <el-select
          v-model="woForm.customerId"
          filterable remote :remote-method="searchCustomers" :loading="customerLoading"
          placeholder="搜索客户名称/手机号" style="width: 100%" @change="onCustomerChange"
        >
          <el-option v-for="c in customerOptions" :key="c.id" :label="`${c.name}${c.phone ? `（${c.phone}）` : ''}`" :value="c.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>关联出库单 *</label>
        <el-select v-model="woForm.outboundId" :placeholder="outboundOptions.length ? '选择出库单' : '该客户暂无已审核/已完成的出库单'" style="width: 100%" :disabled="!woForm.customerId">
          <el-option v-for="o in outboundOptions" :key="o.id" :label="`${o.outboundNo}（${o.status === 'completed' ? '已完成' : '已审核'}）`" :value="o.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>安装师傅</label>
        <el-select v-model="woForm.installerEmployeeId" clearable placeholder="可不选（待派单）" style="width: 100%">
          <el-option v-for="e in candidates?.installerOptions || []" :key="e.id" :label="e.name" :value="e.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>预约日期</label>
        <el-date-picker v-model="woForm.scheduledDate" type="date" value-format="YYYY-MM-DD" placeholder="可选" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>预约时间</label>
        <el-time-picker v-model="woForm.scheduledTime" format="HH:mm" value-format="HH:mm" placeholder="可选" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>安装位置</label>
        <el-input v-model="woForm.installLocation" placeholder="如：客厅、主卧（可选）" />
      </div>
      <div class="form-item">
        <label>备注</label>
        <el-input v-model="woForm.installRemark" type="textarea" :rows="2" placeholder="可选" />
      </div>
    </template>

    <!-- 记录客户跟进 -->
    <template v-else-if="intent === 'create_follow_up'">
      <div class="form-item">
        <label>客户 *</label>
        <el-select
          v-model="fuForm.customerId"
          filterable remote :remote-method="searchCustomers" :loading="customerLoading"
          placeholder="搜索客户名称/手机号" style="width: 100%"
        >
          <el-option v-for="c in customerOptions" :key="c.id" :label="`${c.name}${c.phone ? `（${c.phone}）` : ''}`" :value="c.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>跟进方式</label>
        <el-select v-model="fuForm.type" style="width: 100%">
          <el-option label="电话" value="phone" />
          <el-option label="拜访" value="visit" />
          <el-option label="微信" value="wechat" />
          <el-option label="邮件" value="email" />
          <el-option label="其他" value="other" />
        </el-select>
      </div>
      <div class="form-item">
        <label>跟进内容 *</label>
        <el-input v-model="fuForm.content" type="textarea" :rows="3" placeholder="跟进内容" />
      </div>
      <div class="form-item">
        <label>下次跟进日期</label>
        <el-date-picker v-model="fuForm.nextFollowUpDate" type="date" value-format="YYYY-MM-DD" placeholder="可选（选了会自动生成跟进计划）" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>下次跟进计划</label>
        <el-input v-model="fuForm.nextPlanContent" placeholder="可选" />
      </div>
      <div v-if="!self?.employeeId" class="warn-tip">你的账号未绑定员工档案，无法提交跟进记录，请联系管理员在用户管理中绑定</div>
    </template>

    <!-- 添加收付款记录（票据识别） -->
    <template v-else-if="intent === 'create_payment'">
      <div class="form-item">
        <label>类型</label>
        <el-radio-group v-model="payForm.type">
          <el-radio-button value="receive">收款</el-radio-button>
          <el-radio-button value="pay">付款</el-radio-button>
        </el-radio-group>
      </div>
      <div class="form-item">
        <label>{{ payForm.type === 'receive' ? '客户（付款人）' : '供应商（收款人）' }}</label>
        <el-select
          v-if="payForm.type === 'receive'"
          v-model="payForm.customerId"
          filterable remote clearable :remote-method="searchCustomers" :loading="customerLoading"
          placeholder="搜索客户名称/手机号（可不选）" style="width: 100%"
        >
          <el-option v-for="c in customerOptions" :key="c.id" :label="`${c.name}${c.phone ? `（${c.phone}）` : ''}`" :value="c.id" />
        </el-select>
        <el-select
          v-else
          v-model="payForm.supplierId"
          filterable remote clearable :remote-method="searchSuppliers" :loading="supplierLoading"
          placeholder="搜索供应商名称（可不选）" style="width: 100%"
        >
          <el-option v-for="s in supplierOptions" :key="s.id" :label="s.name" :value="s.id" />
        </el-select>
        <div v-if="params.counterpartyName" class="hint-tip">识别到对方：{{ params.counterpartyName }}</div>
      </div>
      <div class="form-item">
        <label>收付账户 *</label>
        <el-select v-model="payForm.accountId" placeholder="选择账户" style="width: 100%">
          <el-option v-for="a in candidates?.accounts || []" :key="a.id" :label="`${a.name}（${accountTypeMap[a.type] || a.type}）`" :value="a.id" />
        </el-select>
        <div v-if="(candidates?.accounts || []).length === 0" class="warn-tip">没有可用账户：出纳账户需先在资金账户中指定你为出纳</div>
      </div>
      <div class="form-item">
        <label>金额 *</label>
        <el-input-number v-model="payForm.amount" :min="0" :precision="2" :controls="false" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>支付方式 *</label>
        <el-select v-model="payForm.paymentMethod" placeholder="选择支付方式" style="width: 100%">
          <el-option label="微信" value="wechat" />
          <el-option label="支付宝" value="alipay" />
          <el-option label="银行转账" value="transfer" />
          <el-option label="现金" value="cash" />
          <el-option label="支票" value="check" />
        </el-select>
      </div>
      <div class="form-item">
        <label>收付日期 *</label>
        <el-date-picker v-model="payForm.paymentDate" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>流水号</label>
        <el-input v-model="payForm.transactionNo" placeholder="交易单号（可选）" />
      </div>
      <div class="form-item">
        <label>备注</label>
        <el-input v-model="payForm.remark" placeholder="可选" />
      </div>
    </template>

    <!-- 创建报销单草稿（票据识别） -->
    <template v-else-if="intent === 'create_expense'">
      <div class="form-item">
        <label>费用类型 *</label>
        <el-select v-model="expForm.categoryId" filterable placeholder="选择费用类型" style="width: 100%">
          <el-option
            v-for="c in candidates?.expenseCategories || []"
            :key="c.id"
            :label="`${c.parentId ? '　' : ''}${c.name}`"
            :value="c.id"
          />
        </el-select>
      </div>
      <div class="form-item">
        <label>金额 *</label>
        <el-input-number v-model="expForm.amount" :min="0" :precision="2" :controls="false" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>费用日期 *</label>
        <el-date-picker v-model="expForm.itemDate" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
      </div>
      <div class="form-item">
        <label>摘要</label>
        <el-input v-model="expForm.description" placeholder="费用说明（可选）" />
      </div>
      <div class="form-item">
        <label>付款账户</label>
        <el-select v-model="expForm.accountId" clearable placeholder="可后续补充" style="width: 100%">
          <el-option v-for="a in candidates?.accounts || []" :key="a.id" :label="`${a.name}（${accountTypeMap[a.type] || a.type}）`" :value="a.id" />
        </el-select>
      </div>
      <div class="form-item">
        <label>备注</label>
        <el-input v-model="expForm.remark" placeholder="可选" />
      </div>
      <div v-if="!self?.employeeId" class="warn-tip">你的账号未绑定员工档案，无法创建报销单，请联系管理员在用户管理中绑定</div>
      <div v-else class="hint-tip">报销人：{{ self.employeeName || '我' }}（创建为草稿，可在费用报销中继续完善后提交）</div>
    </template>

    <!-- 操作按钮 -->
    <div v-if="status === 'pending'" class="card-actions">
      <el-button size="small" @click="$emit('cancel')">取消</el-button>
      <el-button size="small" type="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">确认创建</el-button>
    </div>
    <div v-else class="card-result" :class="status">{{ resultText }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { createTodo } from '@/api/todo'
import { createWorkOrder } from '@/api/work-order'
import { addCustomerFollowUp } from '@/api/follow-up'
import { getCustomerList } from '@/api/customer'
import { getOutboundList } from '@/api/outbound'
import { searchSupplierOptions } from '@/api/supplier'
import { createPayment } from '@/api/finance'
import { createExpenseClaim } from '@/api/expense-claim'
import { uploadImage } from '@/api/upload'
import type { AiCommandIntent, AiCandidateCustomer, AiCandidateEmployee, AiCandidateSupplier, AiCandidateAccount, AiCandidateExpenseCategory } from '@/api/ai'

const props = defineProps<{
  intent: Exclude<AiCommandIntent, 'none'>
  params: any
  candidates?: {
    customers?: AiCandidateCustomer[]
    installers?: AiCandidateEmployee[]
    installerOptions?: AiCandidateEmployee[]
    assignees?: AiCandidateEmployee[]
    suppliers?: AiCandidateSupplier[]
    accounts?: AiCandidateAccount[]
    expenseCategories?: AiCandidateExpenseCategory[]
  }
  self?: { userId: number; employeeId: number | null; employeeName: string | null }
  status: 'pending' | 'done' | 'cancelled'
  resultText?: string
  voucherFile?: File // 票据原图，确认创建时上传为业务凭证
}>()

const emit = defineEmits<{
  (e: 'done', text: string): void
  (e: 'cancel'): void
}>()

const titleMap: Record<string, string> = {
  create_todo: '创建待办',
  create_work_order: '创建安装工单',
  create_follow_up: '记录客户跟进',
  create_payment: '添加收付款记录',
  create_expense: '创建报销单草稿'
}

const accountTypeMap: Record<string, string> = {
  cash: '现金',
  bank: '银行',
  wechat: '微信',
  alipay: '支付宝'
}

// 本地今天 YYYY-MM-DD
const todayStr = new Date().toLocaleDateString('sv-SE')

// ===== 待办表单 =====
const todoForm = ref({
  title: props.params.title || '',
  description: props.params.description || '',
  priority: props.params.priority || 'medium',
  dueDate: props.params.dueDate || '',
  dueTime: props.params.dueTime || '',
  assigneeId: props.candidates?.assignees?.[0]?.id ?? 0 // 0=自己
})

// ===== 工单表单 =====
const woForm = ref({
  customerId: props.candidates?.customers?.[0]?.id ?? (null as number | null),
  outboundId: null as number | null,
  installerEmployeeId: props.candidates?.installers?.[0]?.id ?? (null as number | null),
  scheduledDate: props.params.scheduledDate || '',
  scheduledTime: props.params.scheduledTime || '',
  installLocation: props.params.installLocation || '',
  installRemark: props.params.installRemark || ''
})

// ===== 跟进表单 =====
const fuForm = ref({
  customerId: props.candidates?.customers?.[0]?.id ?? (null as number | null),
  type: props.params.type || 'phone',
  content: props.params.content || '',
  nextFollowUpDate: props.params.nextFollowUpDate || '',
  nextPlanContent: props.params.nextPlanContent || ''
})

// ===== 收付款表单（票据识别） =====
const payForm = ref({
  type: (props.params.voucherType === 'pay' ? 'pay' : 'receive') as 'receive' | 'pay',
  customerId: (props.candidates?.customers?.[0]?.id ?? null) as number | null,
  supplierId: (props.candidates?.suppliers?.[0]?.id ?? null) as number | null,
  accountId: (props.candidates?.accounts?.[0]?.id ?? null) as number | null,
  amount: props.params.amount || 0,
  paymentMethod: props.params.paymentMethod || '',
  paymentDate: props.params.paymentDate || todayStr,
  transactionNo: props.params.transactionNo || '',
  remark: props.params.summary || ''
})

// ===== 报销单表单（票据识别） =====
const expForm = ref({
  categoryId: null as number | null,
  amount: props.params.amount || 0,
  itemDate: props.params.paymentDate || todayStr,
  description: props.params.summary || '',
  accountId: null as number | null,
  remark: props.params.counterpartyName ? `商家：${props.params.counterpartyName}` : ''
})

// ===== 供应商远程搜索 =====
const supplierOptions = ref<AiCandidateSupplier[]>(props.candidates?.suppliers || [])
const supplierLoading = ref(false)
const searchSuppliers = async (keyword: string) => {
  if (!keyword) {
    supplierOptions.value = props.candidates?.suppliers || []
    return
  }
  supplierLoading.value = true
  try {
    const res: any = await searchSupplierOptions({ keyword, page: 1, pageSize: 20 })
    supplierOptions.value = (res.data?.list || []).map((s: any) => ({ id: s.id, name: s.name, contactName: s.contactName, phone: s.phone }))
  } finally {
    supplierLoading.value = false
  }
}

// 票据原图上传为业务凭证（收付款 images / 报销 invoiceImage）
const uploadVoucher = async (folder: string): Promise<string | undefined> => {
  if (!props.voucherFile) return undefined
  const res: any = await uploadImage(props.voucherFile, folder)
  return res.data?.data?.url
}

// ===== 客户远程搜索 =====
const customerOptions = ref<AiCandidateCustomer[]>(props.candidates?.customers || [])
const customerLoading = ref(false)
const searchCustomers = async (keyword: string) => {
  if (!keyword) {
    customerOptions.value = props.candidates?.customers || []
    return
  }
  customerLoading.value = true
  try {
    const res = await getCustomerList({ keyword, page: 1, pageSize: 20 })
    customerOptions.value = (res.list || []).map((c: any) => ({ id: c.id, name: c.name, phone: c.phone, address: c.address }))
  } finally {
    customerLoading.value = false
  }
}

// ===== 出库单（工单必须关联，按客户联动） =====
const outboundOptions = ref<{ id: number; outboundNo: string; status: string }[]>([])
const loadOutbounds = async (customerId: number) => {
  outboundOptions.value = []
  woForm.value.outboundId = null
  try {
    const res: any = await getOutboundList({ customerId, page: 1, pageSize: 50 })
    const list = res.data?.data?.list || []
    outboundOptions.value = list
      .filter((o: any) => ['approved', 'completed'].includes(o.status))
      .map((o: any) => ({ id: o.id, outboundNo: o.outboundNo, status: o.status }))
    if (outboundOptions.value.length === 1) {
      woForm.value.outboundId = outboundOptions.value[0].id
    }
  } catch {
    // 列表加载失败由用户手动排查
  }
}
const onCustomerChange = (id: number | null) => {
  if (id) loadOutbounds(id)
}
// 工单：初始已预选客户时自动带出库单
if (props.intent === 'create_work_order' && woForm.value.customerId) {
  loadOutbounds(woForm.value.customerId)
}

// ===== 提交 =====
const submitting = ref(false)

const canSubmit = computed(() => {
  if (props.intent === 'create_todo') return !!todoForm.value.title.trim()
  if (props.intent === 'create_work_order') return !!(woForm.value.customerId && woForm.value.outboundId)
  if (props.intent === 'create_follow_up') {
    return !!(fuForm.value.customerId && fuForm.value.content.trim() && props.self?.employeeId)
  }
  if (props.intent === 'create_payment') {
    return !!(payForm.value.accountId && payForm.value.amount > 0 && payForm.value.paymentMethod && payForm.value.paymentDate)
  }
  if (props.intent === 'create_expense') {
    return !!(expForm.value.categoryId && expForm.value.amount > 0 && expForm.value.itemDate && props.self?.employeeId)
  }
  return false
})

const submit = async () => {
  if (submitting.value || !canSubmit.value) return
  submitting.value = true
  try {
    if (props.intent === 'create_todo') {
      const f = todoForm.value
      await createTodo({
        title: f.title.trim(),
        description: f.description.trim() || undefined,
        priority: f.priority as any,
        dueDate: f.dueDate || undefined,
        dueTime: f.dueTime || undefined,
        assigneeType: f.assigneeId === 0 ? 'user' : 'employee',
        assigneeId: f.assigneeId === 0 ? props.self?.userId : f.assigneeId
      })
      emit('done', `待办「${f.title.trim()}」创建成功`)
    } else if (props.intent === 'create_work_order') {
      const f = woForm.value
      const scheduledAt = f.scheduledDate
        ? `${f.scheduledDate} ${f.scheduledTime || '09:00'}:00`
        : undefined
      const res: any = await createWorkOrder({
        customerId: f.customerId!,
        outboundId: f.outboundId!,
        type: 'install',
        installerEmployeeId: f.installerEmployeeId || undefined,
        scheduledAt,
        installLocation: f.installLocation.trim() || undefined,
        installRemark: f.installRemark.trim() || undefined,
        source: 'internal'
      })
      emit('done', `安装工单 ${res.data?.data?.orderNo || ''} 创建成功`)
    } else if (props.intent === 'create_follow_up') {
      const f = fuForm.value
      await addCustomerFollowUp(f.customerId!, {
        employeeId: props.self!.employeeId!,
        type: f.type as any,
        content: f.content.trim(),
        nextFollowUpDate: f.nextFollowUpDate || undefined,
        nextPlanContent: f.nextPlanContent.trim() || undefined
      })
      emit('done', '跟进记录已保存' + (f.nextFollowUpDate ? '，并已生成下次跟进计划' : ''))
    } else if (props.intent === 'create_payment') {
      const f = payForm.value
      const imageUrl = await uploadVoucher('payment-vouchers')
      await createPayment({
        type: f.type,
        customerId: f.type === 'receive' ? (f.customerId || undefined) : undefined,
        supplierId: f.type === 'pay' ? (f.supplierId || undefined) : undefined,
        accountId: f.accountId!,
        amount: f.amount,
        actualAmount: f.amount,
        paymentMethod: f.paymentMethod as any,
        transactionNo: f.transactionNo.trim() || undefined,
        paymentDate: f.paymentDate,
        remark: f.remark.trim() || undefined,
        images: imageUrl ? [imageUrl] : undefined
      })
      emit('done', `${f.type === 'receive' ? '收款' : '付款'}记录 ¥${f.amount.toFixed(2)} 已保存`)
    } else if (props.intent === 'create_expense') {
      const f = expForm.value
      const imageUrl = await uploadVoucher('expense-vouchers')
      await createExpenseClaim({
        employeeId: props.self!.employeeId!,
        categoryId: f.categoryId!,
        accountId: f.accountId || undefined,
        items: [{
          itemDate: f.itemDate,
          amount: f.amount,
          description: f.description.trim() || undefined,
          invoiceImage: imageUrl
        }],
        totalAmount: f.amount,
        remark: f.remark.trim() || undefined
      })
      emit('done', `报销单草稿 ¥${f.amount.toFixed(2)} 已创建，可在费用报销中继续完善后提交`)
    }
  } catch {
    // 业务接口报错由 axios 拦截器统一提示
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.action-card {
  margin-top: 8px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 12px;
  min-width: 260px;
}
.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}
.form-item {
  margin-bottom: 10px;
}
.form-item label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
.warn-tip {
  font-size: 12px;
  color: #e6a23c;
  line-height: 1.5;
  margin-bottom: 8px;
}
.hint-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}
.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
.card-result {
  font-size: 13px;
  font-weight: 500;
}
.card-result.done {
  color: #67c23a;
}
.card-result.cancelled {
  color: #909399;
}
</style>
